// src/components/CampaignWizard/StepReview.tsx
import React, { useState } from "react";
import {
	Row,
	Col,
	Card,
	CardBody,
	CardHeader,
	Badge,
	ListGroup,
	ListGroupItem,
	TabContent,
	TabPane,
	Nav,
	NavItem,
	NavLink,
} from "reactstrap";
import { ChannelType, CHANNEL_LABELS } from "./StepChannels";
import { AudienceData } from "store/campaigns/types";
import { Template } from "store/templates/types";

const recipientOptions = [
	{
		label: "Alice Johnson",
		vars: {
			name: "Alice Johnson",
			first_name: "Alice",
			last_name: "Johnson",
			country: "India",
			gender: "Female",
			AGENCY_NAME: "Your Consultants"
		}
	},
	{
		label: "Bob Smith",
		vars: {
			name: "Bob Smith",
			first_name: "Bob",
			last_name: "Smith",
			country: "Nepal",
			gender: "Male",
			AGENCY_NAME: "Your Consultants"
		}
	},
	{
		label: "Priya Patel",
		vars: {
			name: "Priya Patel",
			first_name: "Priya",
			last_name: "Patel",
			country: "UK",
			gender: "Female",
			AGENCY_NAME: "Your Consultants"
		}
	}
];

// Utility function from earlier
function replaceVars(template: string, vars: Record<string, string | undefined>) {
	if (!template) return "";
	return template.replace(/{{\s*([^}]+)\s*}}/g, (_, key) => {
		const v = vars[key.trim()];
		return v === undefined ? `{{${key}}}` : v;
	});
}

export interface StepReviewProps {
	basicInfo: { name: string; description: string; campaign_type: string };
	channels: any[];
	audience: AudienceData;
	schedule: { immediate: boolean; scheduledTime: Date | null; localTime: boolean; recurrence?: any };
	fallbackRules: Array<{ fromChannel: string; onEvents: string[]; toChannel: string; delayHours: number }>;
	costEstimate: { emailCost: number; smsCost: number; inAppCost: number; totalCost: number };
	previewData: { emailHtml: string; smsText: string; inApp: { title: string; body: string } };
	templates: Template[] | null;
}

const StepReview: React.FC<StepReviewProps> = ({
	basicInfo,
	channels,
	audience,
	schedule,
	fallbackRules,
	costEstimate,
	previewData,
	templates
}) => {
	const enabled = channels.map((c) => c.channel);
	const scheduleLabel = schedule.immediate
		? "Immediately"
		: schedule.scheduledTime
			? `${schedule.scheduledTime.toLocaleString()}${schedule.localTime ? " (Local)" : ""}`
			: "—";
	const [previewTab, setPreviewTab] = React.useState<string>(enabled[0] || "EMAIL");
	const [recipient, setRecipient] = useState(recipientOptions[0]);

	// Use prop if passed, else state
	const vars = recipient.vars;

	// Helper to get the real template for a channel
	const getTemplateForChannel = (ch: string) => {
		const channelObj = channels.find(c => c.channel === ch);
		if (!channelObj || !channelObj.template_summary?.id) return null;
		return templates?.find(t => t.id === channelObj.template_summary.id);
	};

	// Generate preview per channel/tab
	const getPreviewContent = (ch: string) => {
		const tpl = getTemplateForChannel(ch);
		if (!tpl || !tpl.valuesJson) return null;
		const content = tpl.valuesJson;
		if (ch === "EMAIL") {
			return <div
				className="border rounded p-3"
				style={{ minHeight: 120, overflowY: "auto", maxHeight: 300 }}
				dangerouslySetInnerHTML={{
					__html: replaceVars(content.template || content.body || content.html || "", vars)
				}}
			/>;
		}
		if (ch === "SMS" || ch === "WHATSAPP") {
			return (
				<pre className="border rounded p-3" style={{ minHeight: 120, whiteSpace: "pre-wrap" }}>
					{replaceVars(content.template || content.body || content.text || "", vars)}
				</pre>
			);
		}
		if (ch === "IN_APP") {
			return (
				<div>
					<h6>{replaceVars(content.title || "", vars)}</h6>
					<p>{replaceVars(content.template || content.body || "", vars)}</p>
				</div>
			);
		}
		return <div className="text-muted">No template selected or no content available.</div>;
	};

	return (
		<>
			<h4 className="mb-5 text-primary fw-bold">Review Your Campaign</h4>

			<Row className="gx-4 gy-4">
				{/* Details */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Details</strong>
						</CardHeader>
						<CardBody className="pt-3">
							<ListGroup flush>
								<ListGroupItem className="px-0 py-1">
									<strong>Name:</strong> {basicInfo.name}
								</ListGroupItem>
								<ListGroupItem className="px-0 py-1">
									<strong>Type:</strong> {basicInfo.campaign_type}
								</ListGroupItem>
								{basicInfo.description && (
									<ListGroupItem className="px-0 py-1 text-muted">
										{basicInfo.description}
									</ListGroupItem>
								)}
							</ListGroup>
						</CardBody>
					</Card>
				</Col>

				{/* Channels */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Channels</strong>
						</CardHeader>
						<CardBody className="pt-3">
							{channels.length > 0 ? (
								channels.map((c: any) => (
									<div key={c.channel} className="mb-3 d-flex align-items-center">
										{c.install_summary?.logo && (
											<img
												src={c.install_summary.logo}
												alt={c.install_summary.app_name}
												width={20}
												height={20}
												className="me-2"
												style={{ borderRadius: 4 }}
											/>
										)}
										<span className="fw-semibold">{c.install_summary?.app_name || c.channel}</span>
										<span className="text-muted small ms-2">{c.install_summary?.account_name}</span>
										{c.template_summary?.name && (
											<Badge color="info" className="ms-2">
												{c.template_summary.name}
											</Badge>
										)}
									</div>
								))
							) : (
								<span className="text-muted">None selected</span>
							)}
						</CardBody>
					</Card>
				</Col>


				{/* Audience */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Audience</strong>
						</CardHeader>
						<CardBody className="pt-3">
							<div>
								<strong>
									{audience.type === "STATIC" ? "Static CSV" : "Dynamic Filter"}
								</strong>
							</div>
							<div className="mt-2 text-muted">{audience.type === "STATIC"
								? `CSV (${audience.imported_count ?? 0} rows`
								: `Filter (${audience?.filter_groups?.length} groups`}</div>
						</CardBody>
					</Card>
				</Col>

				{/* Schedule */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Schedule</strong>
						</CardHeader>
						<CardBody className="pt-3">{scheduleLabel}</CardBody>
					</Card>
				</Col>

				{/* Fallback Rules */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Fallback Rules</strong>
						</CardHeader>
						<CardBody className="pt-3">
							{fallbackRules.length > 0 ? (
								fallbackRules.map((r, i) => (
									<div key={i} className="mb-2">
										<strong>
											{CHANNEL_LABELS[r.fromChannel as ChannelType]} →{" "}
											{CHANNEL_LABELS[r.toChannel as ChannelType]}
										</strong>
										<div className="small text-muted">
											On {r.onEvents.join(", ") || "any"} after {r.delayHours}h
										</div>
									</div>
								))
							) : (
								<span className="text-muted">None defined</span>
							)}
						</CardBody>
					</Card>
				</Col>

				{/* Cost */}
				<Col md={6} lg={4}>
					<Card className="h-100 border-0 shadow-sm">
						<CardHeader className="bg-light border-0">
							<strong>Estimated Cost</strong>
						</CardHeader>
						<CardBody className="pt-3">
							<div className="d-flex justify-content-between mb-1">
								<small>Email:</small>
								<small>${costEstimate.emailCost.toFixed(2)}</small>
							</div>
							<div className="d-flex justify-content-between mb-1">
								<small>SMS:</small>
								<small>${costEstimate.smsCost.toFixed(2)}</small>
							</div>
							<div className="d-flex justify-content-between mb-1">
								<small>In-App:</small>
								<small>${costEstimate.inAppCost.toFixed(2)}</small>
							</div>
							<hr className="my-2" />
							<div className="d-flex justify-content-between fw-bold">
								<span>Total:</span>
								<span>${costEstimate.totalCost.toFixed(2)}</span>
							</div>
						</CardBody>
					</Card>
				</Col>

				{/* Live Preview */}
				{enabled.length > 0 && (
					<Col md={12}>
						<Card className="border-0 shadow-sm">
							<CardHeader className="bg-light border-0 d-flex align-items-center">
								<strong className="me-3">Preview</strong>
								{/* Recipient select for personalization */}
								<div className="me-3" style={{ minWidth: 230 }}>
									<select
										className="form-select"
										value={recipient.label}
										onChange={e => setRecipient(recipientOptions.find(r => r.label === e.target.value)!)}
									>
										{recipientOptions.map(opt =>
											<option key={opt.label} value={opt.label}>{opt.label}</option>
										)}
									</select>
								</div>
								<Nav pills>
									{enabled.map((ch) => (
										<NavItem key={ch}>
											<NavLink
												className={previewTab === ch ? "active" : ""}
												onClick={() => setPreviewTab(ch)}
											>
												{channels.find((c) => c.channel === ch)?.install_summary?.logo && (
													<img
														src={channels.find((c) => c.channel === ch)?.install_summary.logo}
														alt=""
														width={16}
														height={16}
														className="me-1"
													/>
												)}
												{CHANNEL_LABELS[ch as ChannelType]}
											</NavLink>
										</NavItem>
									))}
								</Nav>
							</CardHeader>
							<CardBody className="pt-3">
								<TabContent activeTab={previewTab}>
									{enabled.map(ch => (
										<TabPane tabId={ch} key={ch}>
											{getPreviewContent(ch)}
										</TabPane>
									))}
								</TabContent>
							</CardBody>
						</Card>
					</Col>
				)}

			</Row>
		</>
	);
};

export default StepReview;
